import React, { lazy, Suspense, useEffect } from "react";
import "./App.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Sidebar from "./components/toast/Sidebar";
import Loader from "./components/Loader";
import PreInvoices from "./components/PreInvoices";
import { logout } from "./redux/features/slices/user";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import socket from "./utils/socketService";
import { apiSlice } from "./redux/features/apiSlices/apiSlice";
// import UrlProtectedRoute from './components/toast/UrlProtectedRoutes';

// Lazy load components
const Overview = lazy(() => import("./components/Overview"));
const Login = lazy(() => import("./pages/user/Login"));
const Register = lazy(() => import("./pages/user/Register"));
const OrderBooking = lazy(() => import("./pages/distribution/OrderBooking"));
const ProductTypes = lazy(() => import("./pages/product/ProductTypes"));
const ProductTypeView = lazy(() => import("./pages/product/ProductTypeView"));
const BrandName = lazy(() => import("./pages/product/BrandName"));
const BrandView = lazy(() => import("./pages/product/BrandView"));
const CategoryName = lazy(() => import("./pages/product/CategoryName"));
const CategoryView = lazy(() => import("./pages/product/CategoryView"));
const SubCategory = lazy(() => import("./pages/product/SubCategory"));
const SubCategoryView = lazy(() => import("./pages/product/SubCategoryView"));
const Units = lazy(() => import("./pages/product/Units"));
const UnitView = lazy(() => import("./pages/product/UnitView"));
const Product = lazy(() => import("./pages/product/Product"));
const PurchaseOrder = lazy(() => import("./pages/purchase/PurchaseOrder"));
const Vendor = lazy(() => import("./pages/setup/Vendor"));
const Warehouse = lazy(() => import("./pages/setup/Warehouse"));
const ViewProducts = lazy(() => import("./pages/product/ViewProducts"));
const AddPurchase = lazy(() => import("./pages/purchase/AddPurchase"));
const PurchaseInvoice = lazy(() => import("./pages/purchase/PurchaseInvoice"));
const Inventory = lazy(() => import("./pages/Inventory/Inventory"));
const StockValue = lazy(() => import("./pages/stocks/StockValue"));
const StockAdjustment = lazy(() => import("./pages/stocks/StockAdjustment"));
const Customers = lazy(() => import("./pages/setup/Customers"));
const VendorView = lazy(() => import("./pages/setup/VendorView"));
const CustomerCategory = lazy(() => import("./pages/setup/CustomerCategory"));
const CustomerCategoryView = lazy(
  () => import("./pages/setup/CustomerCategoryView"),
);
const AddTown = lazy(() => import("./pages/setup/AddTown"));
const TownsView = lazy(() => import("./pages/setup/TownsView"));
const WarehouseView = lazy(() => import("./pages/setup/WarehouseView"));
const AddSaleman = lazy(() => import("./pages/setup/AddSaleman"));
const SalemanView = lazy(() => import("./pages/setup/SalemanView"));
const CustomersView = lazy(() => import("./pages/setup/CustomersView"));
const RecieveVoucher = lazy(() => import("./pages/payments/RecieveVoucher"));
const JournalPayment = lazy(() => import("./pages/payments/JournalPayment"));
const JournalPaymentView = lazy(
  () => import("./pages/payments/JournalPaymentView"),
);
const AddPattyCategory = lazy(() => import("./pages/setup/AddPattyCategory"));
const PattyCategoryView = lazy(() => import("./pages/setup/PattyCategoryView"));
const PattyPayment = lazy(() => import("./pages/payments/PattyPayment"));
const PattyExpenceView = lazy(
  () => import("./pages/payments/PattyExpenceView"),
);
const PurchaseReturnView = lazy(
  () => import("./pages/purchase/PurchaseReturnView"),
);
const PurchaseOrdersView = lazy(
  () => import("./pages/purchase/PurchaseOrdersView"),
);
const PaymentVoucher = lazy(() => import("./pages/payments/PaymentVoucher"));
const GstType = lazy(() => import("./pages/setup/GstType"));
const GstTypeView = lazy(() => import("./pages/setup/GstTypeView"));
const ScheduleType = lazy(() => import("./pages/setup/ScheduleType"));
const ScheduleTypeView = lazy(() => import("./pages/setup/ScheduleTypeView"));
const AdvanceTax = lazy(() => import("./pages/setup/AdvanceTax"));
const ReceivePaymentsView = lazy(
  () => import("./pages/payments/ReceivePaymentsView"),
);
const PurchaseInvoiceView = lazy(
  () => import("./pages/purchase/PurchaseInvoiceView"),
);
const PurchasePaymentView = lazy(
  () => import("./pages/payments/PurchasePaymentView"),
);
const PurchaseInvoiceReturn = lazy(
  () => import("./pages/purchase/PurchaseInvoiceReturn"),
);
const PackType = lazy(() => import("./pages/product/PackType"));
const PackTypeView = lazy(() => import("./pages/product/PackTypeView"));
const ChartOfAccounts = lazy(() => import("./pages/accounts/ChartOfAccounts"));
const PaymentMethods = lazy(() => import("./pages/settings/PaymentMethods"));
const AddDevice = lazy(() => import("./pages/setup/AddDevice"));
const DeviceView = lazy(() => import("./pages/setup/DeviceView"));
const AdvanceTaxView = lazy(() => import("./pages/setup/AdvanceTaxView"));
const UserRolePermissionView = lazy(
  () => import("./pages/setup/UserRolePermissionView"),
);

const RoleAndPermissions = lazy(
  () => import("./pages/user/RoleAndPermissions"),
);
const CompanyView = lazy(() => import("./pages/setup/CompanyView"));

// Reports
const LoadSheetReport = lazy(() => import("./pages/reports/LoadSheetReport"));
const SaleNPurchaseReport = lazy(
  () => import("./pages/reports/SaleNPurchaseReport"),
);
const StockLedgerReport = lazy(
  () => import("./pages/reports/StockLedgerReport"),
);
const CustomerReport = lazy(() => import("./pages/reports/CustomerReport"));
const ItemListReport = lazy(() => import("./pages/reports/ItemListReport"));
const LastPurchaseSaleReport = lazy(
  () => import("./pages/reports/LastPurchaseSaleReport"),
);
const DailyStockReport = lazy(() => import("./pages/reports/DailyStockReport"));
const ProductWiseSale = lazy(() => import("./pages/reports/ProductWiseSale"));
const StockPosition = lazy(() => import("./pages/reports/StockPosition"));
const GrossProfitReport = lazy(
  () => import("./pages/reports/GrossProfitReport"),
);
const AllSaleInvoices = lazy(() => import("./pages/reports/AllSaleInvoices"));
const YearlyExpenses = lazy(() => import("./pages/reports/YearlyExpenses"));
const MaxSaleProduct = lazy(() => import("./pages/reports/MaxSaleProduct"));
const UnsoldProducts = lazy(() => import("./pages/reports/UnsoldProducts"));
const DailySalesReport = lazy(() => import("./pages/reports/DailySalesReport"));
const AgingReceiveableReport = lazy(
  () => import("./pages/reports/AgingReceiveableReport"),
);
const AgingPayableReport = lazy(
  () => import("./pages/reports/AgingPayableReport"),
);
const AccountLedgerReport = lazy(
  () => import("./pages/reports/AccountLedgerReport"),
);
const OutstandingReport = lazy(
  () => import("./pages/reports/OutstandingReport"),
);
const PDCReport = lazy(() => import("./pages/reports/PDCReport"));
const DailyTransactionReport = lazy(
  () => import("./pages/reports/DailyTransactionReport"),
);
const TrialBalanceReport = lazy(
  () => import("./pages/reports/TrialBalanceReport"),
);
const NotAuthorized = lazy(() => import("./pages/user/NotAuthorized"));
const CompanySetup = lazy(() => import("./pages/setup/CompanySetup"));
const Pos = lazy(() => import("./pages/pos/Pos"));
const SaleInvoiceView = lazy(
  () => import("./pages/distribution/SaleInvoiceView"),
);
const SaleReturn = lazy(() => import("./pages/pos/SaleReturnPage"));
const SaleReturnView = lazy(
  () => import("./pages/distribution/SaleReturnView"),
);
const BarcodeGenerator = lazy(() => import("./components/BarcodeGenerator"));
const ProductViewer = lazy(() => import("./components/ProductViewer"));
const Quotation = lazy(() => import("./pages/distribution/Quotation"));
const QuotationView = lazy(() => import("./pages/distribution/QuotationView"));
const PaymentView = lazy(() => import("./pages/settings/PaymentView"));
const PendingFbrInvoices = lazy(() => import("./pages/pos/PendingFbrInvoices"));

// HR System Pages
const HrDepartments = lazy(() => import("./pages/hr/Departments"));
const HrShifts = lazy(() => import("./pages/hr/Shifts"));
const HrDesignations = lazy(() => import("./pages/hr/Designations"));
const HrPolicies = lazy(() => import("./pages/hr/Policies"));
const HrLeaves = lazy(() => import("./pages/hr/Leaves"));
const HrHolidays = lazy(() => import("./pages/hr/Holidays"));
const HrEmployees = lazy(() => import("./pages/hr/Employees"));
const HrAttendance = lazy(() => import("./pages/hr/Attendance"));
const HrAttendanceCalendar = lazy(
  () => import("./pages/hr/AttendanceCalendar"),
);
const HrPayroll = lazy(() => import("./pages/hr/Payroll"));
const HrStaffCheckInOutReport = lazy(
  () => import("./pages/hr/StaffCheckInOutReport"),
);
// LOYALTY POINTS & BIOMETRIC DEVICE IMPLEMENTATION START
const BiometricDevices = lazy(() => import("./pages/hr/BiometricDevices"));
// LOYALTY POINTS & BIOMETRIC DEVICE IMPLEMENTATION END

const HrPunch = lazy(() => import("./pages/hr/EmployeePunch"));
const HrCalendar = lazy(() => import("./pages/hr/Calendar"));
const HrAssets = lazy(() => import("./pages/hr/Assets"));
const HrDashboard = lazy(() => import("./pages/hr/Dashboard"));
const HrAppraisals = lazy(() => import("./pages/hr/Appraisals"));
const HrRecruitment = lazy(() => import("./pages/hr/Recruitment"));

// Group routes by category
const settingsRoutes = [
  { path: "/payment-method", Component: PaymentMethods },
  { path: "/payment-view", Component: PaymentView },
  { path: "/barcode-generator", Component: BarcodeGenerator },
  { path: "/product-viewer", Component: ProductViewer },
  { path: "/users", Component: lazy(() => import("./pages/settings/Users")) },
  { path: "/register", Component: Register },
  { path: "/role-permission", Component: RoleAndPermissions },
  { path: "/company-setup", Component: CompanySetup },
  { path: "/company-view", Component: CompanyView },
  { path: "/not-authorized", Component: NotAuthorized },
];

const productRoutes = [
  { path: "/product-type", Component: ProductTypes },
  { path: "/producttype-view", Component: ProductTypeView },
  { path: "/add-brand", Component: BrandName },
  { path: "/brand-view", Component: BrandView },
  { path: "/add-category", Component: CategoryName },
  { path: "/category-view", Component: CategoryView },
  { path: "/add-subcategory", Component: SubCategory },
  { path: "/subcategory-view", Component: SubCategoryView },
  { path: "/add-unit", Component: Units },
  { path: "/unit-view", Component: UnitView },
  { path: "/add-product", Component: Product },
  { path: "/product", Component: ViewProducts },
  { path: "/scan", Component: lazy(() => import("./components/ScanBarcode")) },
  { path: "/packtype", Component: PackType },
  { path: "/packtype-view", Component: PackTypeView },
];

const setupRoutes = [
  { path: "/vendor", Component: Vendor },
  { path: "/warehouse", Component: Warehouse },
  { path: "/customers", Component: Customers },

  { path: "/vendor-view", Component: VendorView },
  { path: "/add-customer-category", Component: CustomerCategory },
  { path: "/customer-view-category", Component: CustomerCategoryView },
  { path: "/add-town", Component: AddTown },
  { path: "/town-view", Component: TownsView },
  { path: "/warehouse-view", Component: WarehouseView },
  { path: "/saleman-view", Component: SalemanView },
  { path: "/add-saleman", Component: AddSaleman },
  { path: "/customers-view", Component: CustomersView },
  { path: "/add-patty-category", Component: AddPattyCategory },
  { path: "/patty-category-view", Component: PattyCategoryView },
  { path: "/gst-type", Component: GstType },
  { path: "/gst-type-view", Component: GstTypeView },
  { path: "/advance-tax", Component: AdvanceTax },
  { path: "/advance-tax-view", Component: AdvanceTaxView },
  { path: "/schedule-type", Component: ScheduleType },
  { path: "/schedule-type-view", Component: ScheduleTypeView },
  { path: "/add-device", Component: AddDevice },
  { path: "/device-view", Component: DeviceView },
  { path: "/role-permission-view", Component: UserRolePermissionView },
];

const purchaseRoutes = [
  { path: "/purchase-order", Component: PurchaseOrder },
  { path: "/add-purchase", Component: AddPurchase },
  { path: "/purchase-invoice", Component: PurchaseInvoice },
  { path: "/purchase-return", Component: PurchaseInvoiceReturn },
  { path: "/purchase-return-view", Component: PurchaseReturnView },
  { path: "/purchase-orders-view", Component: PurchaseOrdersView },
  { path: "/purchase-invoice-view", Component: PurchaseInvoiceView },
];

const inventoryRoutes = [{ path: "/get-inventory", Component: Inventory }];

const stocksRoutes = [
  { path: "/stockvalue", Component: StockValue },
  { path: "/stockadjustment", Component: StockAdjustment },
];

const orderBookingRoutes = [{ path: "/orderbooking", Component: OrderBooking }];

const paymentsRoutes = [
  { path: "/payment-voucher", Component: PaymentVoucher },
  { path: "/recieve-voucher", Component: RecieveVoucher },
  { path: "/journal-payment", Component: JournalPayment },
  { path: "/patty-payment", Component: PattyPayment },
  { path: "/journal-payment-view", Component: JournalPaymentView },
  { path: "/patty-payment-view", Component: PattyExpenceView },
  { path: "/receive-payment-view", Component: ReceivePaymentsView },
  { path: "/purchase-payment-view", Component: PurchasePaymentView },
];

const accountsRoutes = [
  { path: "/chart-of-accounts", Component: ChartOfAccounts },
];

const reportsRoutes = [
  { path: "/loadsheet", Component: LoadSheetReport },
  { path: "/sale-n-purchase", Component: SaleNPurchaseReport },
  { path: "/stockledger", Component: StockLedgerReport },
  { path: "/customerreport", Component: CustomerReport },
  { path: "/itemlist", Component: ItemListReport },
  { path: "/last-purchase-sale", Component: LastPurchaseSaleReport },
  { path: "/daily-stock", Component: DailyStockReport },
  { path: "/product-wise-sale", Component: ProductWiseSale },
  { path: "/stock-position", Component: StockPosition },
  { path: "/gross-profit-report", Component: GrossProfitReport },
  { path: "/all-sale-invoices", Component: AllSaleInvoices },
  { path: "/yearly-expenses", Component: YearlyExpenses },
  { path: "/max-sale-product", Component: MaxSaleProduct },
  { path: "/unsold-products", Component: UnsoldProducts },
  { path: "/daily-sales", Component: DailySalesReport },
  { path: "/aging-receiveable", Component: AgingReceiveableReport },
  { path: "/aging-payable", Component: AgingPayableReport },
  { path: "/account-ledger", Component: AccountLedgerReport },
  { path: "/outstanding-report", Component: OutstandingReport },
  { path: "/pdc-report", Component: PDCReport },
  { path: "/transaction-report", Component: DailyTransactionReport },
  { path: "/trial-balance", Component: TrialBalanceReport },
  {
    path: "/stock-ledger-summary",
    Component: lazy(() => import("./pages/reports/StockLedgerSummaryReport")),
  },
];

const hrRoutes = [
  { path: "/hr-departments", Component: HrDepartments },
  { path: "/hr-shifts", Component: HrShifts },
  { path: "/hr-designations", Component: HrDesignations },
  { path: "/hr-policies", Component: HrPolicies },
  { path: "/hr-leaves", Component: HrLeaves },
  { path: "/hr-holidays", Component: HrHolidays },
  { path: "/hr-employees", Component: HrEmployees },
  { path: "/hr-attendance", Component: HrAttendance },
  { path: "/hr-attendance-calendar", Component: HrAttendanceCalendar },
  { path: "/hr-payroll", Component: HrPayroll },
  { path: "/hr-checkinout-report", Component: HrStaffCheckInOutReport },
  { path: "/hr-punch", Component: HrPunch },
  { path: "/hr-calendar", Component: HrCalendar },
  { path: "/hr-assets", Component: HrAssets },
  { path: "/hr-dashboard", Component: HrDashboard },
  { path: "/hr-appraisals", Component: HrAppraisals },
  { path: "/hr-recruitment", Component: HrRecruitment },
  // LOYALTY POINTS & BIOMETRIC DEVICE IMPLEMENTATION START
  { path: "/hr-biometric-devices", Component: BiometricDevices },
  // LOYALTY POINTS & BIOMETRIC DEVICE IMPLEMENTATION END
];

const renderRoutes = (routes) => {
  return routes.map(({ path, Component }) => (
    <Route
      key={path}
      path={path}
      element={
        <Suspense fallback={<LoadingFallback />}>
          <Component />
        </Suspense>
      }
    />
  ));
};

const LoadingFallback = () => (
  <div className="w-full h-screen flex justify-center items-center">
    <Loader />
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");

  return token ? children : <Navigate to="/login" />;
};

const App = () => {
  const { modes, sideBarCollapes, phoneToggle } = useSelector(
    (state) => state.mode,
  );
  const { isAuthenticated, user } = useSelector((state) => state.user); // Get auth status
  const { company } = useSelector((state) => state.setup);
  const appTitle = company?.company;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const faviconUrl = `${process.env.REACT_APP_BASE_URL}/${company?.logo}`;
  useEffect(() => {
    document.title = appTitle;

    const favicon =
      document.querySelector("link[rel='icon']") ||
      document.createElement("link");

    favicon.rel = "icon";
    favicon.href = faviconUrl;

    document.head.appendChild(favicon);
  }, [appTitle, faviconUrl, company]);

  // Real-time synchronization across 6-8 connected laptops/clients
  useEffect(() => {
    const handleProductsUpdated = () => {
      console.log("⚡ Real-time socket event: products_updated");
      dispatch(apiSlice.util.invalidateTags(["products", "inventory"]));
    };

    const handleInventoryUpdated = () => {
      console.log("⚡ Real-time socket event: inventory_updated");
      dispatch(apiSlice.util.invalidateTags(["inventory", "products"]));
    };

    const handleInvoicesUpdated = () => {
      console.log("⚡ Real-time socket event: invoices_updated");
      dispatch(
        apiSlice.util.invalidateTags(["invoices", "inventory", "products"]),
      );
    };

    socket.on("products_updated", handleProductsUpdated);
    socket.on("inventory_updated", handleInventoryUpdated);
    socket.on("invoices_updated", handleInvoicesUpdated);

    return () => {
      socket.off("products_updated", handleProductsUpdated);
      socket.off("inventory_updated", handleInventoryUpdated);
      socket.off("invoices_updated", handleInvoicesUpdated);
    };
  }, [dispatch]);

  // const lastActivityUpdate = React.useRef(Date.now());

  // const resetTimer = () => {
  //   const now = Date.now();
  //   // Preformance ke liye: Sirf har 15 seconds baad localStorage update hogi
  //   if (now - lastActivityUpdate.current < 15000) return;

  //   const token = localStorage.getItem("token");
  //   if (token) {
  //     lastActivityUpdate.current = now;
  //     const idleMinutes = 5; // 5 minute ka idle timeout
  //     const newExpiry = now + (idleMinutes * 60 * 1000);
  //     localStorage.setItem("token_expiry", newExpiry.toString());
  //   }
  // };

  function autoRemoveExpiredToken() {
    const expiry = localStorage.getItem("token_expiry");
    if (!expiry) return;
    const now = new Date().getTime();
    if (now > parseInt(expiry)) {
      localStorage.removeItem("token");
      localStorage.removeItem("token_expiry");
      dispatch(logout());
      navigate("/login");
    }
  }

  // useEffect(() => {
  //   // Activity Listeners
  //   window.addEventListener("mousemove", resetTimer);
  //   window.addEventListener("keypress", resetTimer);
  //   window.addEventListener("click", resetTimer);
  //   window.addEventListener("scroll", resetTimer);

  //   return () => {
  //     window.removeEventListener("mousemove", resetTimer);
  //     window.removeEventListener("keypress", resetTimer);
  //     window.removeEventListener("click", resetTimer);
  //     window.removeEventListener("scroll", resetTimer);
  //   };
  // }, []);
  // useEffect(() => {
  //   autoRemoveExpiredToken();
  // }, []);
  useEffect(() => {
    console.log(phoneToggle);
  }, [phoneToggle]);

  // Real-time socket listener for user status changes / deactivation / deletion
  useEffect(() => {
    const handleUserStatusChanged = ({ userId, status }) => {
      const storedUser =
        user ||
        (localStorage.getItem("user")
          ? JSON.parse(localStorage.getItem("user"))
          : null);
      const currentUserId = storedUser?.id;
      if (
        currentUserId &&
        parseInt(currentUserId) === parseInt(userId) &&
        (status === "inactive" || status === "deleted")
      ) {
        dispatch(logout());
        navigate("/login");
      }
    };

    socket.on("user_status_changed", handleUserStatusChanged);

    return () => {
      socket.off("user_status_changed", handleUserStatusChanged);
    };
  }, [user, dispatch, navigate]);

  // Periodic polling check for active user status
  useEffect(() => {
    const checkUserStatus = async () => {
      const storedUser =
        user ||
        (localStorage.getItem("user")
          ? JSON.parse(localStorage.getItem("user"))
          : null);
      const userId = storedUser?.id;
      const token = localStorage.getItem("token");

      if (token && userId) {
        try {
          const baseUrl = process.env.REACT_APP_BASE_URL || "";
          const res = await axios.get(
            `${baseUrl}/api/v1/auth/check-status/${userId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (res.data.success && res.data.status === "inactive") {
            dispatch(logout());
            navigate("/login");
          }
        } catch (error) {
          // Only logout if server explicitly tells us user is inactive
          if (
            error.response?.status === 403 &&
            error.response?.data?.isInactive
          ) {
            dispatch(logout());
            navigate("/login");
          }
        }
      }
    };

    checkUserStatus();

    const statusInterval = setInterval(checkUserStatus, 5000); // Check every 5 seconds
    return () => clearInterval(statusInterval);
  }, [user, dispatch, navigate]);

  React.useEffect(() => {
    document.body.style.backgroundColor =
      modes === "dark" ? "#222222" : "#EDEDED";
  }, [modes]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public Route: Login */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/" />
            ) : (
              <Suspense fallback={<LoadingFallback />}>
                <Login />
              </Suspense>
            )
          }
        />
        {/* <Route
            path="/register"
            element={
              isAuthenticated ? (
                <Navigate to="/login" />
              ) : (
                <Suspense fallback={<LoadingFallback />}>
                  <Register />
                </Suspense>
              )
            }
          /> */}

        {/* Protected Routes: Main App Layout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              {/* <UrlProtectedRoute> */}
              <main
                className={`w-full bg-transparent transition-all duration-150 ease-linear flex`}
              >
                <div className="w-full">
                  <div
                    className={`sidebar fixed top-0 md:left-0  ${phoneToggle === "hide" ? "-left-56" : "left-0"} z-50 `}
                  >
                    <Sidebar />
                  </div>
                  <div
                    className={` content transition-all duration-150 ease-linear w-full ${
                      sideBarCollapes === "show"
                        ? "md:pl-[13.8rem]"
                        : "md:pl-[4.8rem]"
                    }`}
                  >
                    <Routes>
                      <Route path="/" element={<Overview />} />
                      <Route path="/pos" element={<Pos />} />
                      <Route
                        path="/sale-invoice-view"
                        element={<SaleInvoiceView />}
                      />
                      <Route path="/sale-return" element={<SaleReturn />} />
                      <Route
                        path="/sale-return-view"
                        element={<SaleReturnView />}
                      />
                      <Route path="/quotation" element={<Quotation />} />
                      <Route path="/quotation/:id" element={<Quotation />} />
                      <Route
                        path="/quotation-view"
                        element={<QuotationView />}
                      />
                      <Route
                        path="/pending-fbr-invoices"
                        element={<PendingFbrInvoices />}
                      />

                      {renderRoutes(settingsRoutes)}
                      {renderRoutes(productRoutes)}
                      {renderRoutes(setupRoutes)}
                      {renderRoutes(purchaseRoutes)}
                      {renderRoutes(inventoryRoutes)}
                      {renderRoutes(stocksRoutes)}
                      {renderRoutes(orderBookingRoutes)}
                      {renderRoutes(paymentsRoutes)}
                      {renderRoutes(accountsRoutes)}
                      {renderRoutes(reportsRoutes)}
                      {renderRoutes(hrRoutes)}
                    </Routes>
                  </div>
                </div>
              </main>
              {/* </UrlProtectedRoute> */}
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

export default App;
